/**
 * Admin Subscriptions Service
 *
 * Wraps les endpoints super-admin du core-backend pour gerer les souscriptions
 * produit par brand (US-MAC.8).
 *
 * Endpoints :
 *   GET    /admin/brands/:brandId/subscriptions
 *   POST   /admin/brands/:brandId/subscriptions             body: { product, bdisplayScreenLimit? }
 *   DELETE /admin/brands/:brandId/subscriptions/:product
 *   PATCH  /admin/brands/:brandId/bdisplay-screen-limit     body: { limit: number | null }
 */

import { coreApiClient } from '@/api/core-client';
import type { EffectiveSubscription, Product } from '@bill/core-types';

type ApiResponse<T> = { success: true; data: T };

export interface BrandSubscriptionsListResponse {
  brandId: string;
  subscriptions: EffectiveSubscription[];
  bdisplayScreenLimit: number | null;
}

export interface ActivateSubscriptionInput {
  product: Product;
  /** Pour BDISPLAY uniquement : limite d'ecrans posee a l'activation. */
  bdisplayScreenLimit?: number | null;
  metadata?: Record<string, unknown> | null;
}

export const adminSubscriptionsService = {
  async list(brandId: string): Promise<BrandSubscriptionsListResponse> {
    const response = await coreApiClient.get<ApiResponse<BrandSubscriptionsListResponse>>(
      `/admin/brands/${brandId}/subscriptions`,
    );
    return response.data.data;
  },

  async activate(brandId: string, input: ActivateSubscriptionInput): Promise<void> {
    await coreApiClient.post(`/admin/brands/${brandId}/subscriptions`, input);
  },

  async deactivate(brandId: string, product: Product): Promise<void> {
    await coreApiClient.delete(`/admin/brands/${brandId}/subscriptions/${product}`);
  },

  async setBdisplayScreenLimit(
    brandId: string,
    limit: number | null,
  ): Promise<{ brandId: string; bdisplayScreenLimit: number | null }> {
    const response = await coreApiClient.patch<
      ApiResponse<{ brandId: string; bdisplayScreenLimit: number | null }>
    >(`/admin/brands/${brandId}/bdisplay-screen-limit`, { limit });
    return response.data.data;
  },

  async setDemo(brandId: string, product: Product, isDemo: boolean): Promise<void> {
    await coreApiClient.patch(
      `/admin/brands/${brandId}/subscriptions/${product}/demo`,
      { isDemo },
    );
  },
};
